import { useState, useEffect, useRef } from 'react'
import { Car, Clock, RefreshCw } from 'lucide-react'
import { parkingReservationApi } from '../services/api'
import {
  Card,
  CardHeader,
  CardBody,
  LoadingSpinner,
  EmptyState,
  Badge,
  Button,
  FormCheckbox,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell
} from '../components/common'

const formatRelativeTime = (dateString) => {
  if (!dateString) return '—'
  const now = new Date()
  const date = new Date(dateString)
  const diffMs = now - date
  if (diffMs < 0) return 'just now'
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHrs = Math.floor(diffMin / 60)
  if (diffHrs < 24) return `${diffHrs}h ago`
  const diffDays = Math.floor(diffHrs / 24)
  return `${diffDays}d ago`
}

const formatUnitFloor = (unitNumber, floor) => {
  const parts = []
  if (unitNumber) parts.push(`Unit ${unitNumber}`)
  if (floor) parts.push(`Floor ${floor}`)
  return parts.length > 0 ? parts.join(', ') : '—'
}

const ParkingOccupancy = ({ buildingId }) => {
  const [occupancy, setOccupancy] = useState(null)
  const [loading, setLoading] = useState(false)
  const [autoRefresh, setAutoRefresh] = useState(false)
  const intervalRef = useRef(null)

  useEffect(() => {
    if (buildingId) {
      loadOccupancy()
    } else {
      setOccupancy(null)
    }
  }, [buildingId])

  useEffect(() => {
    if (autoRefresh && buildingId) {
      intervalRef.current = setInterval(loadOccupancy, 30000)
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [autoRefresh, buildingId])

  const loadOccupancy = async () => {
    if (!buildingId) return
    try {
      setLoading(true)
      const response = await parkingReservationApi.getOccupancy(buildingId)
      const data = response.data?.data || response.data
      setOccupancy(data)
    } catch (err) {
      console.error('Error loading occupancy:', err)
      setOccupancy(null)
    } finally {
      setLoading(false)
    }
  }

  const getProgressColor = (pct) => {
    if (pct >= 90) return 'bg-red-500'
    if (pct >= 70) return 'bg-amber-500'
    return 'bg-green-500'
  }

  if (!buildingId) return null

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <FormCheckbox
            label="Auto-refresh (30s)"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
        </div>
        <Button
          onClick={loadOccupancy}
          variant="secondary"
          size="small"
          icon={<RefreshCw className="w-4 h-4" />}
        >
          Refresh
        </Button>
      </div>

      {loading && !occupancy ? (
        <LoadingSpinner />
      ) : !occupancy ? (
        <EmptyState
          icon={Car}
          title="No occupancy data"
          description="No parking configuration found for this building."
        />
      ) : (
        <>
          {/* Occupancy Summary */}
          <Card>
            <CardHeader
              title="Current Occupancy"
              subtitle={`Parking Type: ${occupancy.parkingTypeName || 'Unspecified'}`}
            />
            <CardBody>
              <div className="flex items-center gap-8">
                <div className="text-center">
                  <div className="text-4xl font-bold text-gray-900">
                    {occupancy.currentOccupancy}
                  </div>
                  <div className="text-sm text-gray-500">
                    {occupancy.totalCapacity ? `of ${occupancy.totalCapacity}` : 'checked in'}
                  </div>
                </div>

                {occupancy.totalCapacity && (
                  <div className="flex-1">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">Occupancy</span>
                      <span className="font-medium text-gray-900">
                        {occupancy.occupancyPercentage ?? 0}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-4">
                      <div
                        className={`h-4 rounded-full transition-all duration-500 ${getProgressColor(occupancy.occupancyPercentage ?? 0)}`}
                        style={{ width: `${Math.min(100, occupancy.occupancyPercentage ?? 0)}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>

          {/* Spot Grid (SpecificSpot mode only) */}
          {occupancy.spots && occupancy.spots.length > 0 && (
            <Card>
              <CardHeader
                title="Spot Status"
                subtitle={`${occupancy.spots.filter(s => s.isOccupied).length} of ${occupancy.spots.length} spots occupied`}
              />
              <CardBody>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {occupancy.spots.map((spot) => (
                    <div
                      key={spot.parkingSpotId}
                      className={`p-3 rounded-lg border-2 ${
                        spot.isOccupied
                          ? 'border-red-200 bg-red-50'
                          : 'border-green-200 bg-green-50'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-gray-500">
                          {spot.spotNumber || spot.name}
                        </span>
                        <Badge
                          variant={spot.isOccupied ? 'danger' : 'success'}
                          size="small"
                        >
                          {spot.isOccupied ? 'In Use' : 'Free'}
                        </Badge>
                      </div>
                      {spot.floor && (
                        <div className="text-xs text-gray-400">Floor: {spot.floor}</div>
                      )}
                      {spot.isOccupied && (
                        <div className="mt-1 space-y-0.5">
                          {spot.occupantName && (
                            <div className="text-xs text-gray-600 truncate">{spot.occupantName}</div>
                          )}
                          {spot.carPlate && (
                            <div className="text-xs font-mono text-gray-500">{spot.carPlate}</div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          )}

          {/* Active Check-ins Table */}
          {occupancy.activeCheckIns && occupancy.activeCheckIns.length > 0 && (
            <Card>
              <CardHeader
                title="Active Check-ins"
                subtitle={`${occupancy.activeCheckIns.length} vehicle${occupancy.activeCheckIns.length !== 1 ? 's' : ''} currently checked in`}
              />
              <CardBody className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableCell header>Occupant</TableCell>
                      <TableCell header>Car Plate</TableCell>
                      <TableCell header>Unit / Floor</TableCell>
                      {occupancy.parkingTypeName === 'SpecificSpot' && (
                        <TableCell header>Spot</TableCell>
                      )}
                      <TableCell header>Checked In</TableCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {occupancy.activeCheckIns.map((checkIn) => (
                      <TableRow key={checkIn.parkingReservationId}>
                        <TableCell>
                          <span className="font-medium text-gray-900">
                            {checkIn.occupantName || '—'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge variant="primary" size="small">
                            <span className="font-mono">{checkIn.carPlate || '—'}</span>
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {formatUnitFloor(checkIn.unitNumber, checkIn.floor)}
                        </TableCell>
                        {occupancy.parkingTypeName === 'SpecificSpot' && (
                          <TableCell>
                            {checkIn.parkingSpotName || '—'}
                          </TableCell>
                        )}
                        <TableCell>
                          <div className="flex items-center gap-1.5 text-gray-500">
                            <Clock className="w-3.5 h-3.5" />
                            <span>{formatRelativeTime(checkIn.checkedInAt)}</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardBody>
            </Card>
          )}
        </>
      )}
    </div>
  )
}

export default ParkingOccupancy
